import { supabase } from "../lib/supabase";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// ─────────────────────────────────────────────────────────────
// Lecture publique du catalogue (inchangé)
// ─────────────────────────────────────────────────────────────

export async function getPOSCatalog(signal) {
	const response = await fetch(`${API_URL}/api/pos/catalog`, {
		method: "GET",
		headers: { Accept: "application/json" },
		signal,
	});
	if (!response.ok) {
		throw new Error(`Erreur lors du chargement du catalogue POS : ${response.status}`);
	}
	return response.json();
}

export async function getPOSHealth() {
	const response = await fetch(`${API_URL}/api/pos/health`);
	if (!response.ok) throw new Error("Le service POS est indisponible");
	return response.json();
}

// ─────────────────────────────────────────────────────────────
// Opérations de caisse : via la passerelle Supabase « pos-gateway »
// (vérifie la session admin puis relaie vers /api/pos/...)
// ─────────────────────────────────────────────────────────────

async function readError(error) {
	try {
		const response = error?.context;
		if (response && typeof response.json === "function") {
			const body = await response.json();
			if (typeof body?.detail === "string") return body.detail;
			if (Array.isArray(body?.detail) && body.detail[0]?.msg) return body.detail[0].msg;
		}
	} catch {
		// réponse non JSON
	}
	return error?.message || "Erreur de communication avec la caisse.";
}

async function call(method, path, body) {
	const { data, error } = await supabase.functions.invoke("pos-gateway", {
		body: { method, path, body },
	});
	if (error) throw new Error(await readError(error));
	return data;
}

const qs = (params) => {
	const search = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== "") search.append(key, value);
	});
	const text = search.toString();
	return text ? `?${text}` : "";
};

export const pos = {
	currentSession: (registerId) =>
		call("GET", `/api/pos/session/current${qs({ register_id: registerId })}`),
	sessions: () => call("GET", "/api/pos/sessions?limit=30"),
	openSession: (openingCash, registerId) =>
		call("POST", "/api/pos/sessions", { opening_cash: openingCash, register_id: registerId }),
	sessionSummary: (sessionId) => call("GET", `/api/pos/sessions/${sessionId}/summary`),
	cashMovement: (sessionId, movementType, amount, reason) =>
		call("POST", `/api/pos/sessions/${sessionId}/cash-movements`, {
			movement_type: movementType,
			amount,
			reason,
		}),
	closeSession: (sessionId, countedCash, notes) =>
		call("POST", `/api/pos/sessions/${sessionId}/close`, { counted_cash: countedCash, notes }),

	createSale: (payload) => call("POST", "/api/pos/sales", payload),
	getSale: (saleId) => call("GET", `/api/pos/sales/${saleId}`),
	cancelSale: (saleId) => call("POST", `/api/pos/sales/${saleId}/cancel`),
	simulateCard: (saleId) => call("POST", `/api/pos/sales/${saleId}/simulate-card`),
	listSales: (sessionId, method) =>
		call("GET", `/api/pos/sales${qs({ session_id: sessionId, method })}`),

	readers: () => call("GET", "/api/pos/terminal/readers"),
	createSimulatedReader: () => call("POST", "/api/pos/terminal/simulated-reader"),
};

// Terminal choisi sur cet appareil (chaque tablette peut avoir le sien).
const READER_KEY = "legers_pos_reader_id";
export function getSavedReaderId() {
	try {
		return window.localStorage.getItem(READER_KEY) || "";
	} catch {
		return "";
	}
}
export function saveReaderId(readerId) {
	try {
		if (readerId) window.localStorage.setItem(READER_KEY, readerId);
		else window.localStorage.removeItem(READER_KEY);
	} catch {
		// stockage indisponible : le choix sera redemandé
	}
}

export function newClientSaleId() {
	if (window.crypto?.randomUUID) return window.crypto.randomUUID();
	return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
		(c ^ (window.crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16),
	);
}

export const formatCHF = (value) =>
	new Intl.NumberFormat("fr-CH", { style: "currency", currency: "CHF" }).format(Number(value) || 0);

export const METHOD_LABEL = { cash: "Espèces", terminal: "Terminal" };
